<template>
  <div>
    <!-- Google マップ： 未連携 -->
    <RequireGmbConnect v-if="noLinked" />

    <!-- Google マップ： 連携済み&データ取得済み -->
    <CompaniesAnalysisLayout
      v-else
      :not-available-type="analysisAxesLoading ? null : notAvailableType"
      :company-uuid="$route.params.company_uuid"
      :is-more-than-editor="showMoreThanEditorAuthority"
    >
      <!-- ヘッダー -->
      <template #header>
        <CompaniesAnalysisHeader
          :current-date="date"
          :period="period"
          :visible-export-csv="canExportAuthority('analysis/reviews') && providedFeaturesCsvExport"
          :disabled-export-csv="loading"
          :disabled-dates="disabledDates"
          @change-date="changeDate"
          @change-period="changePeriod"
          @export-csv="setExportCsv"
        >
          <template #select>
            <div v-if="!loading" style="width: 240px">
              <OptionGroupSelect :value="dataType" :options="availableDataTypes" @input="changeDataType" />
            </div>
          </template>
        </CompaniesAnalysisHeader>
      </template>

      <!-- グラフ -->
      <template #graph>
        <div class="p-lineChart">
          <div class="p-lineChart__legends">
            <div v-for="item in colorSelectedList" :key="item.id" class="p-lineChart__legends__items">
              <div class="p-lineChart__legends__items--color" :style="`background-color:${item.color};`" />
              <div class="p-lineChart__legends__items--text">{{ item.value }}</div>
            </div>
          </div>
          <KkInsightPolling v-if="polling" class="p-lineChart__polling" />
          <Loading v-if="graphLoading" />
          <ChartLine
            v-else-if="graphType === 'line'"
            class="p-lineChart__container"
            :chart-data="chartData"
            :options="chartOptions"
            :height="360"
          />
          <ChartBar
            v-else-if="graphType === 'bar'"
            class="p-lineChart__container"
            :chart-data="chartData"
            :options="chartOptions"
            :height="360"
          />
        </div>
      </template>

      <!-- 分析軸選択 -->
      <template #terms>
        <CompaniesAnalysisCrossSearch
          v-if="!analysisAxesLoading"
          :disabled="aggsLoading"
          :cross-search-field="crossSearchField"
          :current-page-type="pageType"
          :page-types="pageTypes"
          @set-page-type="changePageType"
          @set-cross-search-field="changeCrossSearchField"
        />
      </template>

      <!-- 現在の表示リスト / 表示リストのリセット -->
      <template #reset>
        <ul v-show="individualAnalysisName" class="c-links">
          <li class="c-links__item">
            <strong @click="changeIndividualAnalysisField(null)">{{ $t('pages.companies.analysis.individual.reset') }}</strong> | {{ $t('pages.companies.analysis.individual.listTitle', { name: individualAnalysisName }) }}
          </li>
        </ul>
      </template>

      <!-- テーブル -->
      <template #table>
        <CompaniesAnalysisTable
          :loading="aggsLoading || analysisAxesLoading"
          :items="aggs"
          :static-heads="dataTypes"
          :cross-search-fields="crossSearchFields"
          :search-items="displayGraphs"
          :truncate-store-name="true"
          @change-display-graphs="changeDisplayGraphs"
          @change-individual-analysis-field="changeIndividualAnalysisField"
        />
      </template>
    </CompaniesAnalysisLayout>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout'
import CompaniesAnalysisHeader from '../../../components/CompaniesAnalysis/Header.vue'
import CompaniesAnalysisTable from '../../../components/CompaniesAnalysis/Table'
import CompaniesAnalysisCrossSearch from '../../../components/CompaniesAnalysis/CrossSearch'
import RequireGmbConnect from '../../../components/RequireGmbConnect'
import OptionGroupSelect from '../../../components/OptionGroupSelect'
import Loading from '../../../components/Loading'
import ChartLine from '../../../components/ChartLine'
import ChartBar from '../../../components/ChartBar'
import { KkInsightPolling } from '../../../components/domains/analysis/insight'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import {
  colors,
  stackedBarFourStepsColorNumbers,
  stackedBarThreeStepsColorNumbers,
} from '../../../modules/analysis'
import { i18n } from '../../../plugins/i18n'
import { summaryCustomTooltip } from '../../../modules/customTooltip.ts'
import { generateGraphId } from '../../../modules/analysis'

export default {
  name: 'CompaniesAnalysisInsight',
  components: {
    CompaniesAnalysisLayout,
    CompaniesAnalysisHeader,
    CompaniesAnalysisTable,
    CompaniesAnalysisCrossSearch,
    RequireGmbConnect,
    OptionGroupSelect,
    Loading,
    ChartLine,
    ChartBar,
    KkInsightPolling
  },
  mixins: [companiesNotAvailableTypeMixin],
  beforeRouteUpdate(to, from, next) {
    if (this.individualAnalysisName) {
      this.setIndividualAnalysisField(null)
      this.setDisplayGraphs([generateGraphId(this.aggs[0])])
    }
    from.query.cross = this.crossSearchField?.fields
    if (this.notAvailableType) this.setCrossSearchField(null)
    to.query.cross = this.crossSearchField?.fields
    this.setPageType(to.query.fields || 'store_id')
    this.availableDataTypes.forEach(type => {
      type.data.forEach(v => {
        if (v.id === to.query.type) this.setDataType(v)
      })
    })
    if (!this.notAvailableType) {
      this.fetchData(this.evalNeedFetchAggsData(to.query, from.query))
    }
    next()
  },
  emits: ['update-title'],
  data () {
    return {
      title: '',
    }
  },
  computed: {
    ...mapState({
      plots: state => state.companiesAnalysisInsight.plots,
      analysisAxesLoading: state => state.companiesAnalysisAxes.loading,
      dataType: state => state.companiesAnalysisInsight.dataType,
      date: state => state.companiesAnalysisInsight.date,
      aggsLoading: state => state.companiesAnalysisInsight.aggsLoading,
      aggsPolling: state => state.companiesAnalysisInsight.aggsPolling,
      plotsLoading: state => state.companiesAnalysisInsight.plotsLoading,
      displayGraphs: state => state.companiesAnalysisInsight.displayGraphs,
      pageType: state => state.companiesAnalysisInsight.pageType,
      graphKey: state => state.companiesAnalysisInsight.graphKey,
      crossSearchField: state => state.companiesAnalysisInsight.crossSearchField,
      noLinked: state => state.companiesAnalysisInsight.noLinked,
      availableDataTypes: state => state.companiesAnalysisInsight.availableDataTypes,
      period: state => state.filters.period
    }),
    ...mapGetters({
      crossSearchFields: 'companiesAnalysisInsight/crossSearchFields',
      individualAnalysisName: 'companiesAnalysisInsight/individualAnalysisName',
      aggs: 'companiesAnalysisInsight/aggs',
      selectedAggIds: 'companiesAnalysisInsight/selectedAggIds',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      pageTypes: 'companiesAnalysisInsight/pageTypes',
      dataTypes: 'companiesAnalysisInsight/dataTypes',
      graphType: 'companiesAnalysisInsight/graphType',
    }),
    /**
     * グラフまたはテーブルデータ取得 API に渡すパラメータ
     * @return { Object }
     */
    requestParams() {
      return {
        companyUuid: this.$route.params.company_uuid,
        params: {
          posted_at_term: `${ moment(this.period.startDate).format(i18n.t('format.date.gmbYYYY-MM-DD')) } - ${ moment(this.period.endDate).format(i18n.t('format.date.gmbYYYY-MM-DD')) }`
        }
      }
    },
    /**
     * テーブルまたはグラフデータ取得中かどうか
     * @return { Boolean }
     */
    loading() {
      return this.aggsLoading || this.plotsLoading || this.aggsPolling
    },
    polling() {
      return this.aggsPolling
    },
    graphLoading() {
      return this.loading || this.analysisAxesLoading
    },
    /**
     * 選択させない期間
     * @return { Object }
     */
    disabledDates() {
      return {
        from: null,
        to: moment()
      }
    },
    colorSelectedList() {
      let localCount = 0
      const barGraphColors = []
      for(const key in this.plots) {
        if (!this.selectedAggIds.includes(key)) continue
        const plot = this.plots[key]
        if (plot == null || this.graphType === 'line') continue
        barGraphColors.push({id:localCount, value: plot.label, color: colors[localCount % 10]})
        localCount++
      }
      return barGraphColors
    },
    /**
     * グラフライブラリに渡すデータ
     * @return { Object }
     */
    chartData() {
      const datasets = []
      let localCount = 0
      let colorNumbers = stackedBarThreeStepsColorNumbers
      for(const key in this.plots) {
        if (!this.selectedAggIds.includes(key)) continue
        const plot = this.plots[key]
        if (plot == null) continue
        if(this.graphType === 'bar') {
          let nameList = []
          let items = []
          if(this.dataType.id === 'businessImpressions') {
            colorNumbers = stackedBarFourStepsColorNumbers
            const dataList = this.availableDataTypes.find(type => type.data.some(v => v.id === 'businessImpressions'))
            nameList = dataList
              .data.filter((item) => item.id !== 'businessImpressions')
              .map(item => item.name)
              .reverse()
            const desktopSearch = Object.values(plot.business_impressions_desktop_search_graph_elements)
            const mobileSearch = Object.values(plot.business_impressions_mobile_search_graph_elements)
            const desktopMaps = Object.values(plot.business_impressions_desktop_maps_graph_elements)
            const mobileMaps = Object.values(plot.business_impressions_mobile_maps_graph_elements)
            // NOTE: 積み上げグラフのツールチップ内の項目が配列の順序に依存している。
            items = [
              [...mobileMaps],
              [...desktopMaps],
              [...mobileSearch],
              [...desktopSearch]
            ]
          } else if (this.dataType.id === 'microConversions') {
            const dataList = this.availableDataTypes.find(type => type.data.some(v => v.id === 'microConversions'))
            nameList = dataList
              .data.filter((item) => item.id !== 'microConversions')
              .map(item => item.name)
              .reverse()
            const businessDirectionRequests = Object.values(plot.business_direction_requests_graph_elements)
            const callClicks = Object.values(plot.call_clicks_graph_elements  )
            const websiteClicks = Object.values(plot.website_clicks_graph_elements)
            items = [[...callClicks],[...businessDirectionRequests],[...websiteClicks]]
          }
          for(let i = 0; i < nameList.length; i++){
            datasets.push({
              label: [plot.label, nameList[i]],
              data: items[i],
              backgroundColor: `rgba(${ colorNumbers[localCount % 10][i] })`,
              lineTension: 0,
              pointRadius: 4,
              stack: `${key}`
            })
          }
          localCount++
        } else {
          const data = Object.values(plot[this.dataType.dataKey])
          datasets.push({
            label: plot.label,
            data,
            backgroundColor: [ 'rgba(255, 255, 255, 0)' ],
            borderColor: [ colors[datasets.length % 10] ],
            borderWidth: 2,
            pointBorderColor:  colors[datasets.length % 10],
            lineTension: 0,
            pointRadius: 4
          })
        }
      }
      return { labels: this.graphKey, datasets }
    },
    /**
     * グラフライブラリに渡すオプション
     * @return { Object }
     */
    chartOptions() {
      const y = { beginAtZero: true }
      const x = {}
      const legend = {}
      const layout = {}
      if (this.dataType.id === 'scores') {
        y.max = 5
      } else if (this.graphType === 'bar'){
        y.stacked = true
        x.stacked = true
        legend.display = false
        layout.padding = { bottom: 30 }
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 4,
        scales: {
          y,
          x
        },
        layout,
        plugins: {
          legend,
          tooltip: this.toolTip
        }
      }
    },
    toolTip() {
      if (this.graphType === 'bar') {
        return {
          mode: 'x',
          enabled: false,
          external: (context) => {
            summaryCustomTooltip(context, this.dataType.name)
          },
        }
      } else {
        return {
          callbacks: {
            label: function(context) {
              if(context.chart.options.scales.x.stacked) return context.dataset.label + ': ' + context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '件'
              return context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
          }
        }
      }
    }
  },
  watch: {
    title(newTitle) {
      this.$emit('update-title', newTitle)
    }
  },
  async created() {
    await this.readCompaniesAnalysisAxes()
    this.initParams(this.$route.query.type)
    if (this.notAvailableType) this.setCrossSearchField(null)
    this.title = this.$t('pages.companies.analysis.insight.title')
    if (this.notAvailableType) return
    await this.fetchData(true, true)
  },
  methods: {
    ...mapActions({
      setDataType: 'companiesAnalysisInsight/setDataType',
      setDate: 'companiesAnalysisInsight/setDate',
      setDisplayGraphs: 'companiesAnalysisInsight/setDisplayGraphs',
      setPageType: 'companiesAnalysisInsight/setPageType',
      setInitialPlots: 'companiesAnalysisInsight/setInitialPlots',
      setCrossSearchField: 'companiesAnalysisInsight/setCrossSearchField',
      setIndividualAnalysisField: 'companiesAnalysisInsight/setIndividualAnalysisField',
      exportCsv: 'companiesAnalysisInsight/exportCsv',
      setPeriod: 'filters/setPeriod',
      readCompaniesAnalysisAxes: 'companiesAnalysisAxes/readCompaniesAnalysisAxes',
      readAggs: 'companiesAnalysisInsight/readAggs',
      readPlots: 'companiesAnalysisInsight/readPlots',
    }),
    /**
     * データ種別の変更
     * @param { Object } value
     * @return { Void }
     */
    changeDataType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, type: value.id } })
    },
    /**
     * 日, 週, 月の変更
     * @param  { String } value
     * @return { Void }
     */
    changeDate(value) {
      if (this.loading) return
      this.setInitialPlots()
      this.setDate(value)
      this.fetchData()
    },
    /**
     * 表のデータの再取得が必要かどうかを判定して返す
     * @return Boolean
     **/
    evalNeedFetchAggsData(toQuery, fromQuery) {
      return toQuery.fields !== fromQuery.fields || toQuery.mode !== fromQuery.mode || toQuery.cross !== fromQuery.cross
    },
    initParams(type) {
      this.setDataType(this.dataTypes.find(v => v.id === type) || this.dataTypes[0])
      this.setPageType(this.$route.query.fields || 'store_id')
    },
    /**
     * APIを叩く
     */
    async fetchData(fetchAggs = false, parallel = false) {
      if (fetchAggs && parallel) {
        this.readAggs(this.requestParams)
        this.readPlots({ params: this.requestParams, parallel: true })
      } else {
        if (fetchAggs) {
          await this.readAggs(this.requestParams)
        }
        if (this.aggs.length) {
          await this.readPlots({ params: this.requestParams })
        }
      }
    },
    /**
     * 期間の変更
     * @param { Object } value
     * @return { Void }
     */
    async changePeriod(value) {
      if (this.loading) return
      this.setPeriod(value)
      this.fetchData(true)
    },
    /**
     * グラフ表示対象の変更
     * @param  { String } value
     * @return { Void }
     */
    changeDisplayGraphs(value) {
      if (this.loading) return
      this.setDisplayGraphs(value)
      this.fetchData()
    },
    /**
     * 分析軸の変更
     * @param  { String } value
     * @return { Void }
     */
    changePageType(value) {
      if (this.loading) return
      this.$router.push({ query: { ...this.$route.query, fields: value.fields } })
    },
    /**
     * クロス分析の変更
     * @param  { String } value
     * @return { Void }
     */
    async changeCrossSearchField(value) {
      if (this.loading) return
      this.setCrossSearchField(value)
      this.fetchData(true)
    },
    /**
     * テーブル表示リストの変更
     * @param  { String } value
     * @return { Void }
     */
    changeIndividualAnalysisField(value) {
      if (this.loading) return
      this.setIndividualAnalysisField(value)
      this.setDisplayGraphs([generateGraphId(this.aggs[0])])
      this.fetchData()
    },
    async setExportCsv() {
      const isSuccess = await this.exportCsv(this.requestParams)
      if (!isSuccess) return
      this.$router.push({ name: 'CompaniesExportResults' })
    },
  },
}
</script>
